/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Alert,
  Dimensions,
  Animated,
  TextInput,
  FlatList,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Platform,
  Image,
  KeyboardAvoidingView,
  TouchableWithoutFeedback,
  Keyboard,
  Linking,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  MaterialIcons,
  Feather,
  AntDesign,
  Entypo,
  FontAwesome,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';

const { width, height } = Dimensions.get('window');

const Forums = () => {
  const navigation = useNavigation();
  const [userData, setUserData] = useState(null);
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTab, setSelectedTab] = useState('trending');
  
  // Forums data
  const [forums, setForums] = useState([]);
  const [myThreads, setMyThreads] = useState([]);
  const [trendingThreads, setTrendingThreads] = useState([]);
  const [bookmarkedThreads, setBookmarkedThreads] = useState([]);
  const [participatingThreads, setParticipatingThreads] = useState([]);
  const [unansweredThreads, setUnansweredThreads] = useState([]);
  const [followedForums, setFollowedForums] = useState([]);
  const [newThreadModalVisible, setNewThreadModalVisible] = useState(false);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [newThreadContent, setNewThreadContent] = useState('');
  const [newThreadCategory, setNewThreadCategory] = useState('general');
  
  // Thread modal states
  const [threadModalVisible, setThreadModalVisible] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadReplies, setThreadReplies] = useState([]);
  const [replyContent, setReplyContent] = useState('');
  
  // Categories
  const categories = [
    { id: 'all', label: 'All Forums', icon: 'grid', color: '#667eea' },
    { id: 'general', label: 'General', icon: 'forum', color: '#4CAF50' },
    { id: 'academic', label: 'Academic', icon: 'school', color: '#2196F3' },
    { id: 'career', label: 'Career', icon: 'briefcase', color: '#FF9800' },
    { id: 'technical', label: 'Technical', icon: 'cog', color: '#9C27B0' },
    { id: 'projects', label: 'Projects', icon: 'lightbulb', color: '#F44336' },
    { id: 'resources', label: 'Resources', icon: 'book-open', color: '#00BCD4' },
  ];
  
  const tabs = [
    { id: 'trending', label: '🔥 Trending', icon: 'fire' },
    { id: 'my-threads', label: '👤 My Threads', icon: 'account' },
    { id: 'bookmarks', label: '⭐ Bookmarks', icon: 'bookmark' },
    { id: 'participating', label: '💬 Participating', icon: 'message-reply' },
    { id: 'unanswered', label: '❓ Unanswered', icon: 'help-circle' },
  ];
  
  const gradients = darkMode ? {
    primary: ['#2d3748', '#4a5568'],
    secondary: ['#805ad5', '#d53f8c'],
    success: ['#38a169', '#68d391'],
    warning: ['#d69e2e', '#ecc94b'],
    danger: ['#e53e3e', '#fc8181'],
    info: ['#3182ce', '#63b3ed'],
    forum: ['#4c51bf', '#667eea'],
  } : {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    forum: ['#667eea', '#764ba2'],
  };

  useEffect(() => {
    loadInitialData();
    startAnimations();
    
    return () => {
      // Cleanup if needed
    };
  }, []);

  useEffect(() => {
    filterThreads();
  }, [searchQuery, selectedCategory, selectedTab]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadUserData(),
        loadForums(),
        loadThreads(),
        loadDarkModePreference(),
      ]);
    } catch (error) {
      console.error('Error loading forum data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        loadForums(),
        loadThreads(),
      ]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadUserData = async () => {
    try {
      const storedUser = await AsyncStorage.getItem('user');
      if (storedUser) {
        setUserData(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadForums = async () => {
    try {
      const mockForums = [
        {
          id: 'F001',
          title: 'Engineering General Discussions',
          description: 'General discussions about engineering studies, careers, and life as an engineering student',
          icon: 'engineering',
          color: '#667eea',
          category: 'general',
          threadCount: 1247,
          memberCount: 5890,
          isFollowing: true,
          lastActivity: new Date(Date.now() - 2 * 60 * 60 * 1000),
          moderators: ['Dr. Sarah Johnson', 'Prof. Robert Williams'],
          rules: [
            'Be respectful to all members',
            'No spam or advertising',
            'Stay on topic',
            'Cite sources when needed'
          ]
        },
        {
          id: 'F002',
          title: 'Academic Support & Homework Help',
          description: 'Get help with coursework, assignments, and understanding complex engineering concepts',
          icon: 'school',
          color: '#4CAF50',
          category: 'academic',
          threadCount: 892,
          memberCount: 3421,
          isFollowing: true,
          lastActivity: new Date(Date.now() - 30 * 60 * 1000),
          moderators: ['Prof. Alex Chen', 'Dr. Maria Rodriguez'],
          rules: [
            'Show your attempt at solving problems',
            'Provide clear problem statements',
            'No direct answers - guide instead',
            'Mark solutions as resolved'
          ]
        },
        {
          id: 'F003',
          title: 'Career & Internship Advice',
          description: 'Discussions about engineering careers, internships, job search, and professional development',
          icon: 'briefcase',
          color: '#FF9800',
          category: 'career',
          threadCount: 567,
          memberCount: 2890,
          isFollowing: false,
          lastActivity: new Date(Date.now() - 5 * 60 * 60 * 1000),
          moderators: ['Career Advisor Mike', 'Industry Expert Lisa'],
          rules: [
            'No recruitment spam',
            'Share real experiences',
            'Respect confidentiality',
            'Be professional'
          ]
        },
        {
          id: 'F004',
          title: 'Technical Problem Solving',
          description: 'Solve complex engineering problems, discuss calculations, and technical challenges',
          icon: 'cog',
          color: '#9C27B0',
          category: 'technical',
          threadCount: 1345,
          memberCount: 4789,
          isFollowing: true,
          lastActivity: new Date(Date.now() - 15 * 60 * 1000),
          moderators: ['Senior Engineer David', 'Tech Lead Sarah'],
          rules: [
            'Include all relevant data',
            'Show calculations',
            'Use proper units',
            'Specify constraints'
          ]
        },
        {
          id: 'F005',
          title: 'Project Ideas & Collaboration',
          description: 'Share project ideas, find collaborators, and discuss engineering projects',
          icon: 'lightbulb',
          color: '#F44336',
          category: 'projects',
          threadCount: 456,
          memberCount: 2345,
          isFollowing: false,
          lastActivity: new Date(Date.now() - 1 * 60 * 60 * 1000),
          moderators: ['Project Manager Tom', 'Senior Designer Amy'],
          rules: [
            'Clearly describe projects',
            'Specify skills needed',
            'Respect intellectual property',
            'Update on progress'
          ]
        },
        {
          id: 'F006',
          title: 'Learning Resources & Books',
          description: 'Share and discuss learning resources, textbooks, online courses, and study materials',
          icon: 'book-open',
          color: '#00BCD4',
          category: 'resources',
          threadCount: 678,
          memberCount: 3123,
          isFollowing: true,
          lastActivity: new Date(Date.now() - 45 * 60 * 1000),
          moderators: ['Librarian Emma', 'Resource Curator John'],
          rules: [
            'Include resource details',
            'Specify free/paid',
            'Add personal reviews',
            'No piracy discussion'
          ]
        },
      ];
      
      setForums(mockForums);
      setFollowedForums(mockForums.filter(f => f.isFollowing));
    } catch (error) {
      console.error('Error loading forums:', error);
    }
  };

  const loadThreads = async () => {
    try {
      const mockTrendingThreads = [
        {
          id: 'T001',
          title: 'How to prepare for structural engineering interviews?',
          content: 'I have an interview next week for a structural engineering position. What are the key topics I should focus on? Any specific technical questions they might ask?',
          forumId: 'F003',
          forumName: 'Career & Internship Advice',
          author: {
            id: 'U001',
            name: 'John Doe',
            avatarColor: '#667eea',
            role: 'Senior Student',
            reputation: 245
          },
          createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000),
          lastReply: new Date(Date.now() - 30 * 60 * 1000),
          replyCount: 24,
          viewCount: 456,
          upvotes: 89,
          isUpvoted: true,
          isBookmarked: false,
          tags: ['interview', 'career', 'preparation'],
          isSolved: false,
          category: 'career'
        },
        {
          id: 'T002',
          title: 'Help with fluid mechanics problem - Bernoulli equation application',
          content: "I'm stuck on this fluid mechanics problem involving the Bernoulli equation. Water flows through a pipe that narrows from 0.1m diameter to 0.05m diameter. The pressure at the wider section is 200 kPa. What's the pressure at the narrow section if the flow rate is 0.02 m³/s?",
          forumId: 'F002',
          forumName: 'Academic Support',
          author: {
            id: 'U002',
            name: 'Sarah Chen',
            avatarColor: '#4CAF50',
            role: 'Junior Student',
            reputation: 89
          },
          createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000),
          lastReply: new Date(Date.now() - 45 * 60 * 1000),
          replyCount: 18,
          viewCount: 321,
          upvotes: 67,
          isUpvoted: false,
          isBookmarked: true,
          tags: ['fluid-mechanics', 'homework', 'bernoulli'],
          isSolved: true,
          category: 'academic'
        },
        {
          id: 'T003',
          title: 'Best free CAD software for mechanical engineering students?',
          content: "I'm looking for recommendations for free CAD software. I need something that's student-friendly but still powerful enough for mechanical design projects. Any suggestions?",
          forumId: 'F006',
          forumName: 'Learning Resources',
          author: {
            id: 'U003',
            name: 'Mike Johnson',
            avatarColor: '#FF9800',
            role: 'Mechanical Student',
            reputation: 156
          },
          createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000),
          lastReply: new Date(Date.now() - 1 * 60 * 60 * 1000),
          replyCount: 42,
          viewCount: 789,
          upvotes: 124,
          isUpvoted: true,
          isBookmarked: true,
          tags: ['cad', 'software', 'mechanical', 'free'],
          isSolved: true,
          category: 'resources'
        },
        {
          id: 'T004',
          title: 'Final year project ideas for electrical engineering',
          content: "I need ideas for my final year project. I'm interested in renewable energy and smart grids. Any innovative project suggestions that are feasible within 6 months?",
          forumId: 'F005',
          forumName: 'Project Ideas',
          author: {
            id: 'U004',
            name: 'Emma Wilson',
            avatarColor: '#9C27B0',
            role: 'Final Year Student',
            reputation: 203
          },
          createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000),
          lastReply: new Date(Date.now() - 2 * 60 * 60 * 1000),
          replyCount: 31,
          viewCount: 567,
          upvotes: 92,
          isUpvoted: false,
          isBookmarked: false,
          tags: ['project', 'electrical', 'renewable-energy', 'ideas'],
          isSolved: false,
          category: 'projects'
        },
        {
          id: 'T005',
          title: 'Stress analysis of cantilever beam with distributed load',
          content: "I'm analyzing a cantilever beam 2m long with a uniformly distributed load of 5 kN/m. Need help calculating maximum bending moment and deflection. Using E = 200 GPa, I = 4.5e-6 m⁴.",
          forumId: 'F004',
          forumName: 'Technical Problems',
          author: {
            id: 'U005',
            name: 'David Brown',
            avatarColor: '#F44336',
            role: 'Civil Student',
            reputation: 178
          },
          createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000),
          lastReply: new Date(Date.now() - 15 * 60 * 1000),
          replyCount: 9,
          viewCount: 234,
          upvotes: 45,
          isUpvoted: true,
          isBookmarked: false,
          tags: ['stress-analysis', 'beam', 'civil', 'calculations'],
          isSolved: false,
          category: 'technical'
        },
        {
          id: 'T006',
          title: 'Transition from engineering to data science - Advice needed',
          content: "I'm a mechanical engineering graduate considering a transition to data science. What skills should I focus on? Are there any specific courses or projects that would help?",
          forumId: 'F001',
          forumName: 'General Discussions',
          author: {
            id: 'U006',
            name: 'Alex Taylor',
            avatarColor: '#00BCD4',
            role: 'Engineering Graduate',
            reputation: 312
          },
          createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000),
          lastReply: new Date(Date.now() - 3 * 60 * 60 * 1000),
          replyCount: 56,
          viewCount: 892,
          upvotes: 167,
          isUpvoted: false,
          isBookmarked: true,
          tags: ['career-change', 'data-science', 'advice', 'transition'],
          isSolved: false,
          category: 'general'
        },
      ];
      
      const mockMyThreads = [
        mockTrendingThreads[0],
        mockTrendingThreads[2],
        {
          id: 'T007',
          title: 'Python libraries for engineering simulations?',
          content: 'Looking for recommendations on Python libraries specifically useful for engineering simulations and calculations.',
          forumId: 'F006',
          forumName: 'Learning Resources',
          author: {
            id: 'U001',
            name: 'John Doe',
            avatarColor: '#667eea',
            role: 'Senior Student',
            reputation: 245
          },
          createdAt: new Date(Date.now() - 48 * 60 * 60 * 1000),
          lastReply: new Date(Date.now() - 24 * 60 * 60 * 1000),
          replyCount: 15,
          viewCount: 198,
          upvotes: 34,
          isUpvoted: true,
          isBookmarked: true,
          tags: ['python', 'simulation', 'libraries'],
          isSolved: true,
          category: 'resources'
        },
      ];
      
      // Mock data for other tabs
      const mockBookmarkedThreads = mockTrendingThreads.filter(t => t.isBookmarked);
      const mockParticipatingThreads = [mockTrendingThreads[0], mockTrendingThreads[1], mockTrendingThreads[3]];
      const mockUnansweredThreads = mockTrendingThreads.filter(t => !t.isSolved);
      
      setTrendingThreads(mockTrendingThreads);
      setMyThreads(mockMyThreads);
      setBookmarkedThreads(mockBookmarkedThreads);
      setParticipatingThreads(mockParticipatingThreads);
      setUnansweredThreads(mockUnansweredThreads);
    } catch (error) {
      console.error('Error loading threads:', error);
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const filterThreads = () => {
    // This function would filter threads based on search query and category
    // For now, we're using mock data
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const getUserName = () => {
    if (!userData) return 'Student';
    const fullName = userData.full_name || userData.fullName || userData.name;
    return fullName || 'Student';
  };

  const toggleFollowForum = async (forumId) => {
    const updatedForums = forums.map(forum => {
      if (forum.id === forumId) {
        return { ...forum, isFollowing: !forum.isFollowing };
      }
      return forum;
    });
    
    setForums(updatedForums);
    setFollowedForums(updatedForums.filter(f => f.isFollowing));
  };

  const toggleUpvoteThread = async (threadId) => {
    const updatedThreads = trendingThreads.map(thread => {
      if (thread.id === threadId) {
        return { 
          ...thread, 
          isUpvoted: !thread.isUpvoted,
          upvotes: thread.isUpvoted ? thread.upvotes - 1 : thread.upvotes + 1
        };
      }
      return thread;
    });
    
    setTrendingThreads(updatedThreads);
  };

  const toggleBookmarkThread = async (threadId) => {
    const updatedThreads = trendingThreads.map(thread => {
      if (thread.id === threadId) {
        const updatedThread = { ...thread, isBookmarked: !thread.isBookmarked };
        
        // Update bookmarked threads list
        if (updatedThread.isBookmarked) {
          setBookmarkedThreads(prev => [...prev, updatedThread]);
        } else {
          setBookmarkedThreads(prev => prev.filter(t => t.id !== threadId));
        }
        
        return updatedThread;
      }
      return thread;
    });
    
    setTrendingThreads(updatedThreads);
  };

  const openThreadModal = (thread) => {
    setSelectedThread(thread);
    loadThreadReplies(thread.id);
    setThreadModalVisible(true);
  };

  const loadThreadReplies = async (threadId) => {
    // Mock replies data
    const mockReplies = [
      {
        id: 'R001',
        content: 'For structural engineering interviews, focus on: 1) Seismic design principles, 2) Load calculations, 3) Building codes (IBC, ASCE), 4) Software proficiency (ETABS, SAP2000), 5) Material properties. Practice explaining your past projects in detail.',
        author: {
          name: 'Senior Structural Engineer',
          avatarColor: '#667eea',
          role: 'Industry Professional',
          reputation: 892
        },
        createdAt: new Date(Date.now() - 2.5 * 60 * 60 * 1000),
        upvotes: 45,
        isUpvoted: true,
        isSolution: false
      },
      {
        id: 'R002',
        content: "Don't forget to prepare for behavioral questions: 'Tell me about a challenging project and how you handled it.' Also, review common structural failures and their causes.",
        author: {
          name: 'Project Manager',
          avatarColor: '#4CAF50',
          role: 'Hiring Manager',
          reputation: 567
        },
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
        upvotes: 32,
        isUpvoted: false,
        isSolution: false
      },
      {
        id: 'R003',
        content: "I recently went through this process. Key technical questions I got: 1) Difference between ASD and LRFD, 2) How to calculate drift, 3) Connection design principles, 4) Foundation types and selection criteria. Good luck!",
        author: {
          name: 'Recent Graduate',
          avatarColor: '#FF9800',
          role: 'Junior Engineer',
          reputation: 123
        },
        createdAt: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
        upvotes: 28,
        isUpvoted: true,
        isSolution: true
      },
    ];
    
    setThreadReplies(mockReplies);
  };

  const postReply = async () => {
    if (!replyContent.trim()) return;
    
    const newReply = {
      id: `R${Date.now()}`,
      content: replyContent,
      author: {
        name: getUserName(),
        avatarColor: '#667eea',
        role: 'Student',
        reputation: userData?.reputation || 100
      },
      createdAt: new Date(),
      upvotes: 0,
      isUpvoted: false,
      isSolution: false
    };
    
    setThreadReplies(prev => [newReply, ...prev]);
    setReplyContent('');
    
    // Update thread reply count
    if (selectedThread) {
      const updatedThreads = trendingThreads.map(thread => {
        if (thread.id === selectedThread.id) {
          return { ...thread, replyCount: thread.replyCount + 1 };
        }
        return thread;
      });
      setTrendingThreads(updatedThreads);
    }
  };

  const createNewThread = async () => {
    if (!newThreadTitle.trim() || !newThreadContent.trim()) {
      Alert.alert('Error', 'Please fill in both title and content');
      return;
    }
    
    const newThread = {
      id: `T${Date.now()}`,
      title: newThreadTitle,
      content: newThreadContent,
      forumId: 'F001',
      forumName: 'General Discussions',
      author: {
        id: 'U001',
        name: getUserName(),
        avatarColor: '#667eea',
        role: 'Student',
        reputation: userData?.reputation || 100
      },
      createdAt: new Date(),
      lastReply: new Date(),
      replyCount: 0,
      viewCount: 0,
      upvotes: 0,
      isUpvoted: false,
      isBookmarked: false,
      tags: [newThreadCategory],
      isSolved: false,
      category: newThreadCategory
    };
    
    setTrendingThreads(prev => [newThread, ...prev]);
    setMyThreads(prev => [newThread, ...prev]);
    
    // Reset form
    setNewThreadTitle('');
    setNewThreadContent('');
    setNewThreadCategory('general');
    setNewThreadModalVisible(false);
    
    Alert.alert('Success', 'Your thread has been posted!');
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    
    let interval = Math.floor(seconds / 31536000);
    if (interval >= 1) return `${interval}y ago`;
    
    interval = Math.floor(seconds / 2592000);
    if (interval >= 1) return `${interval}mo ago`;
    
    interval = Math.floor(seconds / 86400);
    if (interval >= 1) return `${interval}d ago`;
    
    interval = Math.floor(seconds / 3600);
    if (interval >= 1) return `${interval}h ago`;
    
    interval = Math.floor(seconds / 60);
    if (interval >= 1) return `${interval}m ago`;
    
    return 'just now';
  };

  const styles = getStyles(darkMode);

  const renderLoading = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size={36} color={darkMode ? "#667eea" : "#667eea"} />
      <Text style={styles.loadingText}>Loading forums...</Text>
    </View>
  );

  const renderHeader = () => (
    <LinearGradient
      colors={gradients.primary}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton} 
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={28} color="#ffffff" />
      </TouchableOpacity>
      <Text style={styles.headerTitle}>💬 Discussion Forums</Text>
      <TouchableOpacity 
        style={styles.newThreadButton}
        onPress={() => setNewThreadModalVisible(true)}
      >
        <MaterialCommunityIcons name="plus" size={28} color="#ffffff" />
      </TouchableOpacity>
    </LinearGradient>
  );

  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <TouchableOpacity style={styles.searchIcon}>
        <MaterialCommunityIcons name="magnify" size={24} color={darkMode ? "#cbd5e0" : "#6b7280"} />
      </TouchableOpacity>
      <TextInput
        style={styles.searchInput}
        placeholder="Search discussions, topics, or users..."
        value={searchQuery}
        onChangeText={setSearchQuery}
        placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
      />
      {searchQuery.length > 0 && (
        <TouchableOpacity style={styles.clearButton} onPress={() => setSearchQuery('')}>
          <MaterialCommunityIcons name="close-circle" size={20} color={darkMode ? "#cbd5e0" : "#6b7280"} />
        </TouchableOpacity>
      )}
    </View>
  );

  const renderCategoryFilter = () => (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false} 
      style={styles.categoryContainer}
    >
      {categories.map((category) => (
        <TouchableOpacity
          key={category.id}
          style={[
            styles.categoryButton,
            selectedCategory === category.id && styles.categoryButtonActive,
            { borderColor: category.color }
          ]}
          onPress={() => setSelectedCategory(category.id)}
        >
          <MaterialCommunityIcons 
            name={category.icon} 
            size={16} 
            color={selectedCategory === category.id ? '#ffffff' : category.color} 
          />
          <Text style={[
            styles.categoryText,
            selectedCategory === category.id && styles.categoryTextActive,
            selectedCategory !== category.id && { color: category.color }
          ]}>
            {category.label}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderTabNavigation = () => (
    <View style={styles.tabContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabScroll}>
        {tabs.map((tab) => (
          <TouchableOpacity
            key={tab.id}
            style={[
              styles.tabButton,
              selectedTab === tab.id && styles.tabButtonActive,
            ]}
            onPress={() => setSelectedTab(tab.id)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={20} 
              color={selectedTab === tab.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
            />
            <Text style={[
              styles.tabText,
              selectedTab === tab.id && styles.tabTextActive
            ]}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderForumCard = (forum) => (
    <TouchableOpacity
      key={forum.id}
      style={styles.forumCard}
      onPress={() => navigation.navigate('ForumDetails', { forum })}
    >
      <View style={styles.forumHeader}>
        <LinearGradient
          colors={[forum.color, `${forum.color}80`]}
          style={styles.forumIconContainer}
        >
          <MaterialCommunityIcons name={forum.icon} size={24} color="#ffffff" />
        </LinearGradient>
        <View style={styles.forumInfo}>
          <Text style={styles.forumTitle}>{forum.title}</Text>
          <Text style={styles.forumDescription} numberOfLines={2}>
            {forum.description}
          </Text>
        </View>
        <TouchableOpacity 
          style={styles.followButton}
          onPress={() => toggleFollowForum(forum.id)}
        >
          <MaterialCommunityIcons 
            name={forum.isFollowing ? "heart" : "heart-outline"} 
            size={20} 
            color={forum.isFollowing ? "#ef4444" : (darkMode ? "#a0aec0" : "#6b7280")} 
          />
        </TouchableOpacity>
      </View>
      
      <View style={styles.forumStats}>
        <View style={styles.statItem}>
          <MaterialCommunityIcons name="message-text" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
          <Text style={styles.statText}>{forum.threadCount.toLocaleString()} threads</Text>
        </View>
        <View style={styles.statItem}>
          <MaterialCommunityIcons name="account-group" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
          <Text style={styles.statText}>{forum.memberCount.toLocaleString()} members</Text>
        </View>
        <View style={styles.statItem}>
          <MaterialCommunityIcons name="clock" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
          <Text style={styles.statText}>{getTimeAgo(forum.lastActivity)}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderThreadCard = (thread) => (
    <TouchableOpacity
      key={thread.id}
      style={styles.threadCard}
      onPress={() => openThreadModal(thread)}
    >
      <View style={styles.threadHeader}>
        <View style={styles.authorInfo}>
          <LinearGradient
            colors={[thread.author.avatarColor, `${thread.author.avatarColor}80`]}
            style={styles.avatar}
          >
            <Text style={styles.avatarText}>
              {thread.author.name.charAt(0).toUpperCase()}
            </Text>
          </LinearGradient>
          <View>
            <Text style={styles.authorName}>{thread.author.name}</Text>
            <Text style={styles.authorRole}>{thread.author.role}</Text>
          </View>
        </View>
        <View style={styles.threadMeta}>
          <Text style={styles.forumTag}>{thread.forumName}</Text>
          {thread.isSolved && (
            <View style={styles.solvedBadge}>
              <MaterialCommunityIcons name="check-circle" size={12} color="#ffffff" />
              <Text style={styles.solvedText}>Solved</Text>
            </View>
          )}
        </View>
      </View>
      
      <Text style={styles.threadTitle} numberOfLines={2}>{thread.title}</Text>
      <Text style={styles.threadContent} numberOfLines={3}>{thread.content}</Text>
      
      <View style={styles.tagsContainer}>
        {thread.tags.map((tag, index) => (
          <View key={index} style={styles.tag}>
            <Text style={styles.tagText}>#{tag}</Text>
          </View>
        ))}
      </View>
      
      <View style={styles.threadFooter}>
        <View style={styles.threadStats}>
          <TouchableOpacity 
            style={styles.statButton}
            onPress={() => toggleUpvoteThread(thread.id)}
          >
            <MaterialCommunityIcons 
              name={thread.isUpvoted ? "arrow-up-bold" : "arrow-up"} 
              size={16} 
              color={thread.isUpvoted ? "#ef4444" : (darkMode ? "#a0aec0" : "#6b7280")} 
            />
            <Text style={[
              styles.statText,
              thread.isUpvoted && { color: '#ef4444' }
            ]}>{thread.upvotes}</Text>
          </TouchableOpacity>
          
          <View style={styles.statButton}>
            <MaterialCommunityIcons name="message-reply" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.statText}>{thread.replyCount}</Text>
          </View>
          
          <View style={styles.statButton}>
            <MaterialCommunityIcons name="eye" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.statText}>{thread.viewCount}</Text>
          </View>
          
          <Text style={styles.timeAgo}>{getTimeAgo(thread.createdAt)}</Text>
        </View>
        
        <TouchableOpacity 
          style={styles.bookmarkButton}
          onPress={() => toggleBookmarkThread(thread.id)}
        >
          <MaterialCommunityIcons 
            name={thread.isBookmarked ? "bookmark" : "bookmark-outline"} 
            size={20} 
            color={thread.isBookmarked ? "#667eea" : (darkMode ? "#a0aec0" : "#6b7280")} 
          />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  const renderNewThreadModal = () => (
    <Modal
      visible={newThreadModalVisible}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setNewThreadModalVisible(false)}
    >
      <KeyboardAvoidingView 
        style={styles.modalOverlay}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <TouchableWithoutFeedback onPress={() => Keyboard.dismiss()}>
          <View style={styles.modalContent}>
            <LinearGradient
              colors={gradients.primary}
              style={styles.modalHeader}
            >
              <Text style={styles.modalTitle}>Create New Thread</Text>
              <TouchableOpacity 
                onPress={() => setNewThreadModalVisible(false)}
                style={styles.closeButton}
              >
                <MaterialCommunityIcons name="close" size={24} color="#ffffff" />
              </TouchableOpacity>
            </LinearGradient>
            
            <ScrollView style={styles.modalBody}>
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Title</Text>
                <TextInput
                  style={styles.input}
                  placeholder="What's your question or topic?"
                  value={newThreadTitle}
                  onChangeText={setNewThreadTitle}
                  placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
                />
              </View>
              
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Category</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categorySelect}>
                  {categories.filter(c => c.id !== 'all').map((category) => (
                    <TouchableOpacity
                      key={category.id}
                      style={[
                        styles.categoryOption,
                        newThreadCategory === category.id && styles.categoryOptionActive,
                        { borderColor: category.color }
                      ]}
                      onPress={() => setNewThreadCategory(category.id)}
                    >
                      <MaterialCommunityIcons 
                        name={category.icon} 
                        size={16} 
                        color={newThreadCategory === category.id ? '#ffffff' : category.color} 
                      />
                      <Text style={[
                        styles.categoryOptionText,
                        newThreadCategory === category.id && styles.categoryOptionTextActive,
                        newThreadCategory !== category.id && { color: category.color }
                      ]}>
                        {category.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
              
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Content</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  placeholder="Describe your question or topic in detail..."
                  value={newThreadContent}
                  onChangeText={setNewThreadContent}
                  placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
                  multiline
                  numberOfLines={8}
                  textAlignVertical="top"
                />
              </View>
              
              <View style={styles.modalTips}>
                <Text style={styles.tipsTitle}>💡 Tips for better responses:</Text>
                <Text style={styles.tip}>• Be specific and clear</Text>
                <Text style={styles.tip}>• Include relevant details</Text>
                <Text style={styles.tip}>• Format your question well</Text>
                <Text style={styles.tip}>• Check for similar threads first</Text>
              </View>
            </ScrollView>
            
            <View style={styles.modalFooter}>
              <TouchableOpacity 
                style={styles.cancelButton}
                onPress={() => setNewThreadModalVisible(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.postButton}
                onPress={createNewThread}
              >
                <LinearGradient
                  colors={gradients.primary}
                  style={styles.postButtonGradient}
                >
                  <Text style={styles.postButtonText}>Post Thread</Text>
                </LinearGradient>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </KeyboardAvoidingView>
    </Modal>
  );

  const renderThreadModal = () => (
    <Modal
      visible={threadModalVisible}
      animationType="slide"
      onRequestClose={() => setThreadModalVisible(false)}
    >
      <SafeAreaView style={styles.threadModalContainer}>
        <LinearGradient
          colors={gradients.primary}
          style={styles.threadModalHeader}
        >
          <TouchableOpacity 
            onPress={() => setThreadModalVisible(false)}
            style={styles.backButton}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.threadModalTitle} numberOfLines={1}>
            {selectedThread?.title}
          </Text>
          <View style={styles.headerSpacer} />
        </LinearGradient>
        
        <ScrollView style={styles.threadContent}>
          {selectedThread && (
            <>
              <View style={styles.threadHeaderSection}>
                <View style={styles.threadAuthorInfo}>
                  <LinearGradient
                    colors={[selectedThread.author.avatarColor, `${selectedThread.author.avatarColor}80`]}
                    style={styles.largeAvatar}
                  >
                    <Text style={styles.largeAvatarText}>
                      {selectedThread.author.name.charAt(0).toUpperCase()}
                    </Text>
                  </LinearGradient>
                  <View>
                    <Text style={styles.largeAuthorName}>{selectedThread.author.name}</Text>
                    <Text style={styles.largeAuthorRole}>{selectedThread.author.role}</Text>
                    <Text style={styles.reputationText}>
                      <MaterialCommunityIcons name="star" size={12} color="#fbbf24" /> 
                      {selectedThread.author.reputation} reputation
                    </Text>
                  </View>
                </View>
                <View style={styles.threadMetaInfo}>
                  <Text style={styles.timeAgo}>{getTimeAgo(selectedThread.createdAt)}</Text>
                  <View style={styles.forumBadge}>
                    <Text style={styles.forumBadgeText}>{selectedThread.forumName}</Text>
                  </View>
                </View>
              </View>
              
              <Text style={styles.threadFullTitle}>{selectedThread.title}</Text>
              <Text style={styles.threadFullContent}>{selectedThread.content}</Text>
              
              <View style={styles.threadActions}>
                <TouchableOpacity style={styles.actionButton}>
                  <MaterialCommunityIcons name="arrow-up-bold" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.actionText}>Upvote</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.actionButton}>
                  <MaterialCommunityIcons name="bookmark-outline" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.actionText}>Bookmark</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.actionButton}>
                  <MaterialCommunityIcons name="share-variant" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.actionText}>Share</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.actionButton}>
                  <MaterialCommunityIcons name="flag-outline" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={styles.actionText}>Report</Text>
                </TouchableOpacity>
              </View>
              
              <View style={styles.repliesHeader}>
                <Text style={styles.repliesTitle}>
                  💬 {threadReplies.length} {threadReplies.length === 1 ? 'Reply' : 'Replies'}
                </Text>
                <TouchableOpacity style={styles.sortButton}>
                  <Text style={styles.sortText}>Newest First</Text>
                  <MaterialCommunityIcons name="chevron-down" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                </TouchableOpacity>
              </View>
              
              {threadReplies.map((reply) => (
                <View key={reply.id} style={styles.replyCard}>
                  <View style={styles.replyHeader}>
                    <View style={styles.replyAuthor}>
                      <View style={[styles.smallAvatar, { backgroundColor: reply.author.avatarColor }]}>
                        <Text style={styles.smallAvatarText}>
                          {reply.author.name.charAt(0).toUpperCase()}
                        </Text>
                      </View>
                      <View>
                        <Text style={styles.replyAuthorName}>{reply.author.name}</Text>
                        <Text style={styles.replyAuthorRole}>{reply.author.role}</Text>
                      </View>
                    </View>
                    {reply.isSolution && (
                      <View style={styles.solutionBadge}>
                        <MaterialCommunityIcons name="check-circle" size={14} color="#ffffff" />
                        <Text style={styles.solutionText}>Solution</Text>
                      </View>
                    )}
                  </View>
                  <Text style={styles.replyContent}>{reply.content}</Text>
                  <View style={styles.replyFooter}>
                    <Text style={styles.replyTime}>{getTimeAgo(reply.createdAt)}</Text>
                    <View style={styles.replyActions}>
                      <TouchableOpacity style={styles.replyActionButton}>
                        <MaterialCommunityIcons name="arrow-up" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                        <Text style={styles.replyActionText}>{reply.upvotes}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.replyActionButton}>
                        <MaterialCommunityIcons name="reply" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                        <Text style={styles.replyActionText}>Reply</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>
              ))}
            </>
          )}
        </ScrollView>
        
        <View style={styles.replyInputContainer}>
          <TextInput
            style={styles.replyInput}
            placeholder="Write your reply..."
            value={replyContent}
            onChangeText={setReplyContent}
            placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
            multiline
          />
          <TouchableOpacity 
            style={styles.sendReplyButton}
            onPress={postReply}
            disabled={!replyContent.trim()}
          >
            <LinearGradient
              colors={replyContent.trim() ? gradients.primary : ['#e5e7eb', '#f3f4f6']}
              style={styles.sendReplyGradient}
            >
              <MaterialCommunityIcons 
                name="send" 
                size={20} 
                color={replyContent.trim() ? "#ffffff" : "#9ca3af"} 
              />
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );

  const renderWelcomeSection = () => (
    <Animated.View 
      style={[
        styles.welcomeSection,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <LinearGradient
        colors={gradients.primary}
        style={styles.welcomeGradient}
      >
        <Text style={styles.welcomeTitle}>💬 Engineering Forums</Text>
        <Text style={styles.welcomeSubtitle}>
          Connect with {forums.reduce((sum, f) => sum + f.memberCount, 0).toLocaleString()}+ engineering students and professionals
        </Text>
      </LinearGradient>
    </Animated.View>
  );

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'trending':
        return (
          <View style={styles.tabContentContainer}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>🔥 Trending Discussions</Text>
              <Text style={styles.sectionSubtitle}>
                {trendingThreads.length} active threads • {forums.reduce((sum, f) => sum + f.threadCount, 0).toLocaleString()} total threads
              </Text>
            </View>
            
            {trendingThreads.length > 0 ? (
              trendingThreads.map(thread => renderThreadCard(thread))
            ) : (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="fire" size={48} color={darkMode ? "#a0aec0" : "#6b7280"} />
                <Text style={styles.emptyStateText}>No trending threads</Text>
                <Text style={styles.emptyStateSubtext}>
                  Be the first to start a discussion!
                </Text>
              </View>
            )}
          </View>
        );
        
      case 'my-threads':
        return (
          <View style={styles.tabContentContainer}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>👤 My Threads</Text>
              <Text style={styles.sectionSubtitle}>
                {myThreads.length} threads • {myThreads.filter(t => t.isSolved).length} solved
              </Text>
            </View>
            
            {myThreads.length > 0 ? (
              myThreads.map(thread => renderThreadCard(thread))
            ) : (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="message-text-outline" size={48} color={darkMode ? "#a0aec0" : "#6b7280"} />
                <Text style={styles.emptyStateText}>No threads yet</Text>
                <Text style={styles.emptyStateSubtext}>
                  Start a discussion to get help from the community
                </Text>
                <TouchableOpacity 
                  style={styles.createThreadButton}
                  onPress={() => setNewThreadModalVisible(true)}
                >
                  <Text style={styles.createThreadButtonText}>Create First Thread</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        );
        
      case 'bookmarks':
        return (
          <View style={styles.tabContentContainer}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>⭐ Bookmarked Threads</Text>
              <Text style={styles.sectionSubtitle}>
                {bookmarkedThreads.length} saved threads
              </Text>
            </View>
            
            {bookmarkedThreads.length > 0 ? (
              bookmarkedThreads.map(thread => renderThreadCard(thread))
            ) : (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="bookmark-outline" size={48} color={darkMode ? "#a0aec0" : "#6b7280"} />
                <Text style={styles.emptyStateText}>No bookmarked threads</Text>
                <Text style={styles.emptyStateSubtext}>
                  Tap the bookmark icon to save interesting threads
                </Text>
              </View>
            )}
          </View>
        );
        
      case 'participating':
        return (
          <View style={styles.tabContentContainer}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>💬 Participating Threads</Text>
              <Text style={styles.sectionSubtitle}>
                {participatingThreads.length} active discussions
              </Text>
            </View>
            
            {participatingThreads.length > 0 ? (
              participatingThreads.map(thread => renderThreadCard(thread))
            ) : (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="message-reply" size={48} color={darkMode ? "#a0aec0" : "#6b7280"} />
                <Text style={styles.emptyStateText}>Not participating in any threads</Text>
                <Text style={styles.emptyStateSubtext}>
                  Join discussions by replying to threads
                </Text>
              </View>
            )}
          </View>
        );
        
      case 'unanswered':
        return (
          <View style={styles.tabContentContainer}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>❓ Unanswered Threads</Text>
              <Text style={styles.sectionSubtitle}>
                {unansweredThreads.length} threads need answers
              </Text>
            </View>
            
            {unansweredThreads.length > 0 ? (
              unansweredThreads.map(thread => renderThreadCard(thread))
            ) : (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="help-circle" size={48} color={darkMode ? "#a0aec0" : "#6b7280"} />
                <Text style={styles.emptyStateText}>All questions answered!</Text>
                <Text style={styles.emptyStateSubtext}>
                  Great job helping the community
                </Text>
              </View>
            )}
          </View>
        );
        
      default:
        return (
          <View style={styles.tabContentContainer}>
            <Text style={styles.sectionTitle}>Select a tab to view content</Text>
          </View>
        );
    }
  };

  if (loading) {
    return renderLoading();
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#667eea" />
      
      {renderHeader()}
      {renderSearchBar()}
      {renderCategoryFilter()}
      {renderTabNavigation()}
      
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshData}
            colors={['#667eea']}
            tintColor="#667eea"
          />
        }
      >
        {/* Tab-specific content FIRST */}
        {renderTabContent()}
        
        {/* Engineering Forums welcome section - AFTER tab content */}
        <View style={styles.forumsSection}>
          {renderWelcomeSection()}
          
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>🏛️ All Forums</Text>
            <TouchableOpacity onPress={() => navigation.navigate('AllForums')}>
              <Text style={styles.seeAllText}>View All</Text>
            </TouchableOpacity>
          </View>
          
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            style={styles.horizontalScroll}
            contentContainerStyle={styles.horizontalScrollContent}
          >
            {forums.map(forum => renderForumCard(forum))}
          </ScrollView>
        </View>
        
        {/* Statistics section - Show for ALL tabs */}
        <View style={styles.statsSection}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>📈 Forum Statistics</Text>
          </View>
          
          <View style={styles.statsGrid}>
            <View style={styles.statCard}>
              <LinearGradient
                colors={gradients.primary}
                style={styles.statIcon}
              >
                <MaterialCommunityIcons name="message-text" size={24} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.statNumber}>
                {forums.reduce((sum, f) => sum + f.threadCount, 0).toLocaleString()}
              </Text>
              <Text style={styles.statLabel}>Total Threads</Text>
            </View>
            
            <View style={styles.statCard}>
              <LinearGradient
                colors={gradients.success}
                style={styles.statIcon}
              >
                <MaterialCommunityIcons name="account-group" size={24} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.statNumber}>
                {forums.reduce((sum, f) => sum + f.memberCount, 0).toLocaleString()}
              </Text>
              <Text style={styles.statLabel}>Total Members</Text>
            </View>
            
            <View style={styles.statCard}>
              <LinearGradient
                colors={gradients.warning}
                style={styles.statIcon}
              >
                <MaterialCommunityIcons name="fire" size={24} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.statNumber}>
                {trendingThreads.filter(t => t.isSolved).length}
              </Text>
              <Text style={styles.statLabel}>Solved Today</Text>
            </View>
            
            <View style={styles.statCard}>
              <LinearGradient
                colors={gradients.danger}
                style={styles.statIcon}
              >
                <MaterialCommunityIcons name="clock" size={24} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.statNumber}>24/7</Text>
              <Text style={styles.statLabel}>Active</Text>
            </View>
          </View>
        </View>
        
        {/* Quick actions - Show for ALL tabs */}
        <View style={styles.quickActions}>
          <Text style={styles.sectionTitle}>⚡ Quick Actions</Text>
          <View style={styles.actionButtons}>
            <TouchableOpacity 
              style={styles.actionButton}
              onPress={() => setNewThreadModalVisible(true)}
            >
              <LinearGradient
                colors={gradients.primary}
                style={styles.actionButtonIcon}
              >
                <MaterialCommunityIcons name="plus" size={24} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.actionButtonText}>New Thread</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.actionButton}
              onPress={() => setSelectedTab('my-threads')}
            >
              <LinearGradient
                colors={gradients.success}
                style={styles.actionButtonIcon}
              >
                <MaterialCommunityIcons name="message-text" size={24} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.actionButtonText}>My Threads</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.actionButton}
              onPress={() => setSelectedTab('bookmarks')}
            >
              <LinearGradient
                colors={gradients.warning}
                style={styles.actionButtonIcon}
              >
                <MaterialCommunityIcons name="bookmark" size={24} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.actionButtonText}>Bookmarks</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.actionButton}
              onPress={() => navigation.navigate('Notifications')}
            >
              <LinearGradient
                colors={gradients.danger}
                style={styles.actionButtonIcon}
              >
                <MaterialCommunityIcons name="bell" size={24} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.actionButtonText}>Notifications</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      {renderNewThreadModal()}
      {renderThreadModal()}
      
      <TouchableOpacity 
        style={styles.fab}
        onPress={() => setNewThreadModalVisible(true)}
      >
        <LinearGradient
          colors={gradients.primary}
          style={styles.fabGradient}
        >
          <MaterialCommunityIcons name="plus" size={24} color="#ffffff" />
        </LinearGradient>
      </TouchableOpacity>
    </SafeAreaView>
  );
};

const getStyles = (darkMode) => StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
    paddingBottom: Platform.OS === 'ios' ? 0 : 0,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  newThreadButton: {
    padding: 8,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginHorizontal: 20,
    marginVertical: 12,
    borderRadius: 12,
    paddingHorizontal: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  searchIcon: {
    paddingRight: 12,
  },
  searchInput: {
    flex: 1,
    height: 48,
    fontSize: 16,
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  clearButton: {
    padding: 4,
  },
  categoryContainer: {
    paddingHorizontal: 20,
    marginBottom: 12,
    height: 50,
  },
  categoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#e5e7eb',
    height: 40,
  },
  categoryButtonActive: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
  },
  categoryText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  categoryTextActive: {
    color: '#ffffff',
  },
  tabContainer: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  tabScroll: {
    flexDirection: 'row',
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  tabButtonActive: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
  },
  tabText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  tabTextActive: {
    color: '#ffffff',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 10,
    flexGrow: 1,
  },
  welcomeSection: {
    marginHorizontal: 20,
    marginTop: 0,
    marginBottom: 12,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  welcomeGradient: {
    padding: 20,
    borderRadius: 20,
  },
  welcomeTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 6,
  },
  welcomeSubtitle: {
    fontSize: 14,
    color: '#ffffff',
    opacity: 0.9,
  },
  tabContentContainer: {
    marginHorizontal: 20,
    marginBottom: 0,
    marginTop: 4,
  },
  trendingContainer: {
    marginHorizontal: 20,
    marginBottom: 0,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginBottom: 8,
  },
  forumsSection: {
    marginHorizontal: 20,
    marginBottom: 12,
    marginTop: 4,
  },
  statsSection: {
    marginHorizontal: 20,
    marginBottom: 12,
  },
  quickActions: {
    marginHorizontal: 20,
    marginBottom: 12,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  seeAllText: {
    fontSize: 14,
    color: '#667eea',
    fontWeight: '600',
  },
  forumCard: {
    width: 280,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    marginRight: 16,
    marginBottom: 8,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  forumHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  forumIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  forumInfo: {
    flex: 1,
  },
  forumTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  forumDescription: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    lineHeight: 16,
  },
  followButton: {
    padding: 4,
  },
  forumStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statText: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 4,
  },
  threadCard: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginBottom: 6,
    padding: 12,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  threadHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  authorInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  avatarText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  authorName: {
    fontSize: 14,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  authorRole: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  threadMeta: {
    alignItems: 'flex-end',
  },
  forumTag: {
    fontSize: 12,
    color: '#667eea',
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    marginBottom: 4,
  },
  solvedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  solvedText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: '600',
    marginLeft: 2,
  },
  threadTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 8,
  },
  threadContent: {
    fontSize: 13,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginBottom: 12,
    lineHeight: 18,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  tag: {
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 6,
    marginBottom: 6,
  },
  tagText: {
    fontSize: 12,
    color: darkMode ? '#cbd5e0' : '#4b5563',
  },
  threadFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingTop: 12,
  },
  threadStats: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  timeAgo: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  bookmarkButton: {
    padding: 4,
  },
  horizontalScroll: {
    marginHorizontal: -20,
    paddingHorizontal: 20,
  },
  horizontalScrollContent: {
    paddingRight: 20,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  statCard: {
    width: '48%',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    padding: 12,
    borderRadius: 16,
    alignItems: 'center',
    marginBottom: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  statNumber: {
    fontSize: 22,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    marginTop: 12,
    marginBottom: 0,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginTop: 12,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginTop: 6,
    textAlign: 'center',
    marginBottom: 16,
    paddingHorizontal: 20,
  },
  createThreadButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    marginTop: 12,
  },
  createThreadButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 12,
  },
  actionButton: {
    alignItems: 'center',
    flex: 1,
    padding: 8,
  },
  actionButtonIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 6,
  },
  actionButtonText: {
    fontSize: 12,
    color: darkMode ? '#cbd5e0' : '#6b7280',
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: darkMode ? '#1a202c' : '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  closeButton: {
    padding: 4,
  },
  modalBody: {
    padding: 20,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: darkMode ? '#f7fafc' : '#1f2937',
    backgroundColor: darkMode ? '#2d3748' : '#f9fafb',
  },
  textArea: {
    minHeight: 120,
    textAlignVertical: 'top',
  },
  categorySelect: {
    flexDirection: 'row',
  },
  categoryOption: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 8,
  },
  categoryOptionActive: {
    backgroundColor: '#667eea',
  },
  categoryOptionText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  categoryOptionTextActive: {
    color: '#ffffff',
  },
  modalTips: {
    backgroundColor: darkMode ? '#2d3748' : '#f3f4f6',
    padding: 16,
    borderRadius: 12,
    marginTop: 8,
  },
  tipsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 8,
  },
  tip: {
    fontSize: 13,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginBottom: 4,
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    marginRight: 12,
  },
  cancelButtonText: {
    fontSize: 16,
    color: darkMode ? '#a0aec0' : '#6b7280',
    fontWeight: '600',
  },
  postButton: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
  },
  postButtonGradient: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  postButtonText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
  },
  fab: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  fabGradient: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  threadModalContainer: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  threadModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  threadModalTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
    marginHorizontal: 12,
  },
  headerSpacer: {
    width: 40,
  },
  threadContent: {
    flex: 1,
    padding: 16,
  },
  threadHeaderSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  threadAuthorInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  largeAvatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  largeAvatarText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  largeAuthorName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  largeAuthorRole: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginBottom: 2,
  },
  reputationText: {
    fontSize: 12,
    color: '#fbbf24',
  },
  threadMetaInfo: {
    alignItems: 'flex-end',
  },
  forumBadge: {
    backgroundColor: '#667eea',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginTop: 4,
  },
  forumBadgeText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '600',
  },
  threadFullTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 16,
  },
  threadFullContent: {
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    lineHeight: 24,
    marginBottom: 24,
  },
  threadActions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    borderTopWidth: 1,
    borderTopColor: darkMode ? '#4a5568' : '#e5e7eb',
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingVertical: 16,
    marginBottom: 24,
  },
  repliesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  repliesTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  sortButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sortText: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginRight: 4,
  },
  replyCard: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  replyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  replyAuthor: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  smallAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  smallAvatarText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  replyAuthorName: {
    fontSize: 14,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  replyAuthorRole: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  solutionBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 10,
  },
  solutionText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '600',
    marginLeft: 4,
  },
  replyContent: {
    fontSize: 15,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    lineHeight: 22,
    marginBottom: 12,
  },
  replyFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  replyTime: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  replyActions: {
    flexDirection: 'row',
  },
  replyActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 16,
  },
  replyActionText: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 4,
  },
  replyInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderTopWidth: 1,
    borderTopColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  replyInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#d1d5db',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: darkMode ? '#f7fafc' : '#1f2937',
    backgroundColor: darkMode ? '#1a202c' : '#f9fafb',
    marginRight: 12,
  },
  sendReplyButton: {
    borderRadius: 20,
    overflow: 'hidden',
  },
  sendReplyGradient: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default Forums;